import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import {
  FEATURE_KEY_WORKFLOW,
  useFeatureGate,
} from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { Permission } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { type FC } from 'react';

import { WebhookList } from '../automations/webhook-list';
import { SettingsEditorScopeHint } from '../shared/settings-editor-scope-hint';

export const WorkspaceSettingWebhooks: FC = () => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const information = useWorkspaceInfo(workspace);
  const isAdmin = !!(information?.isOwner || information?.isAdmin);
  const isEditor = information?.role === Permission.Editor;
  const { isFeatureAllowed } = useFeatureGate();
  const hasCredentialSharing = isFeatureAllowed('credential-sharing');
  const hasWorkflowFeature = isFeatureAllowed(FEATURE_KEY_WORKFLOW);

  if (!hasWorkflowFeature) {
    return null;
  }

  return (
    <WebhookList
      title={
        t['com.streamlain.settings.workspace.webhooks.title']?.() ?? 'Webhooks'
      }
      subtitle={
        hasCredentialSharing
          ? (t[
              'com.streamlain.settings.workspace.webhooks.section.description.team'
            ]?.() ??
            'Manage inbound webhook endpoints for this workspace here. Endpoint details, sensitive URL material, and access controls stay grouped per webhook.')
          : (t[
              'com.streamlain.settings.workspace.webhooks.section.description.pro'
            ]?.() ??
            'Manage inbound webhook endpoints for this workspace here. Per-endpoint access control is available on Team and above.')
      }
      canManageEndpoints={isAdmin}
      hasAccessControl={hasCredentialSharing}
      canManageAccess={isAdmin}
      footer={isEditor ? <SettingsEditorScopeHint /> : null}
    />
  );
};
